import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Send, Copy, X, Tags, Users, LayoutTemplate, Save, FileDown, CalendarPlus, Eraser } from 'lucide-react';
import {
  getPlannerWeek, publishPlannerWeek, createPlannerBlock, updatePlannerBlock, deletePlannerBlock,
  duplicatePlannerBlock, copyPlannerDay, setPlannerDayGroups, setPlannerSlots, autofillPlannerSlots,
  fillPlannerWeek, savePlannerTemplate, clearPlannerWeek, clearPlannerDay,
} from '../../api';
import { FillFromScenarioDialog, SaveAsTemplateDialog } from './TemplateDialogs';
import PlannerExport from './PlannerExport';
import CalendarFeedLink from './CalendarFeedLink';
import Overlay from './PlannerOverlay';
import BlockPanel from './BlockPanel';
import { FALLBACK_CATEGORY, categoryMap, audienceHint, routineSummary, gymCountsText } from './plannerCategories';
import CategoriesDialog from './CategoriesDialog';
import GroupsPanel from './GroupsPanel';
import { fmtDayShort, fmtRange, fixtureLine, toMin, fromMin, addDays, shiftWeek, isoWeekOf, localToday } from './plannerShared';

// Week view of the Club Planner: 7 day columns on a 07:30–23:00 time grid. Block times, lanes
// (overlap layout), labels and publish changes all come from GET /api/planner/week; this page only
// positions what it gets. Editors can click an empty slot to add a block, click a block to edit it,
// drag it to move (also to another day) and drag its bottom edge to resize.

const GRID_START = 7 * 60 + 30;
const GRID_END = 23 * 60;
const PPM = 1.15;            // pixels per minute
const SNAP = 5;              // drag snap, minutes
const NEW_BLOCK_SNAP = 15;

const STATUS_TEXT = {
  draft: 'Draft · not visible to players yet',
  published: 'Published',
};

function UNRESOLVED_TEXT(reason) {
  if (reason === 'kickoff_unknown') return 'waits for a kickoff time';
  if (reason === 'game_cancelled') return 'its game was cancelled';
  return 'has no game to anchor to';
}

export default function PlannerWeek({ token, isoYear, isoWeek, onChangeWeek, autoFill, onAutoFillDone }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState(null);        // block (existing) or draft
  const [copyDay, setCopyDay] = useState(null);    // target date
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [groupsDate, setGroupsDate] = useState(null);
  const [fillOpen, setFillOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);
  const [preview, setPreview] = useState(null);    // { id, date, start, end } while dragging
  const dragRef = useRef(null);
  const justDraggedRef = useRef(false);
  const columnRefs = useRef({});

  const load = useCallback(async () => {
    try {
      setData(await getPlannerWeek(token, isoYear, isoWeek));
      setError(null);
    } catch (e) {
      setError(e.status === 401 ? 'session' : e.status === 403 ? 'access' : e.message);
    }
  }, [token, isoYear, isoWeek]);

  useEffect(() => { setData(null); load(); }, [load]);

  const canEdit = !!data?.can_edit;
  const cats = useMemo(() => categoryMap(data?.categories), [data]);

  // Arrived from the Upload zone's "Fill the Planner" link (?fill=1): open the fill dialog once.
  useEffect(() => {
    if (autoFill && data && canEdit) { setFillOpen(true); onAutoFillDone(); }
  }, [autoFill, data, canEdit, onAutoFillDone]);

  const run = async (fn, okText) => {
    setBusy(true);
    try {
      const r = await fn();
      if (okText) setNotice({ kind: 'ok', text: typeof okText === 'function' ? okText(r) : okText });
      return r || true;
    } catch (e) {
      setNotice({ kind: 'err', text: e.message });
      return null;
    } finally {
      setBusy(false);
      await load();
    }
  };

  // ── Drag to move / resize ─────────────────────────────────────────────────────────────────
  const columnAt = x => {
    for (const [date, el] of Object.entries(columnRefs.current)) {
      const r = el?.getBoundingClientRect();
      if (r && x >= r.left && x <= r.right) return date;
    }
    return null;
  };

  const onBlockPointerDown = (e, block, mode) => {
    justDraggedRef.current = false;
    if (!canEdit || e.button > 0 || block.resolved_start == null) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      block, mode, x0: e.clientX, y0: e.clientY, moved: false,
      start: toMin(block.resolved_start), end: toMin(block.resolved_end), date: block.date, preview: null,
    };
  };

  const onBlockPointerMove = e => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.x0;
    const dy = e.clientY - d.y0;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    d.moved = true;
    const delta = Math.round(dy / PPM / SNAP) * SNAP;
    let { start, end, date } = d;
    if (d.mode === 'move') {
      const len = end - start;
      start = Math.min(Math.max(0, start + delta), 24 * 60 - len);
      end = start + len;
      if (d.block.timing === 'clock') date = columnAt(e.clientX) || d.date;
    } else {
      end = Math.min(24 * 60, Math.max(start + SNAP, end + delta));
    }
    d.preview = { id: d.block.id, date, start, end };
    setPreview(d.preview);
  };

  const onBlockPointerUp = async () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || !d.moved || !d.preview) return;
    justDraggedRef.current = true;
    const p = d.preview;
    if (p.date === d.date && p.start === d.start && p.end === d.end) { setPreview(null); return; }
    let body;
    if (d.block.timing === 'clock') body = { date: p.date, start_time: fromMin(p.start), end_time: fromMin(p.end) };
    else if (d.mode === 'move') body = { ko_offset_min: d.block.ko_offset_min + (p.start - d.start) };
    else body = { duration_min: p.end - p.start };
    await run(() => updatePlannerBlock(token, d.block.id, body));
    setPreview(null);
  };

  const onBlockClick = block => {
    if (justDraggedRef.current) { justDraggedRef.current = false; return; }
    setPanel(block);
  };

  const onColumnClick = (e, date) => {
    if (!canEdit || e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const minute = GRID_START + Math.floor((e.clientY - rect.top) / PPM / NEW_BLOCK_SNAP) * NEW_BLOCK_SNAP;
    const start = Math.min(minute, GRID_END - 60);
    setPanel({ date, start_time: fromMin(start), end_time: fromMin(start + 60) });
  };

  // ── Panel actions ─────────────────────────────────────────────────────────────────────────
  const saveBlock = async body => {
    const ok = await run(() => (panel.id ? updatePlannerBlock(token, panel.id, body) : createPlannerBlock(token, body)), panel.id ? 'Block saved.' : 'Block added.');
    if (ok) setPanel(null);
  };
  const saveSlots = windows => run(() => setPlannerSlots(token, panel.id, windows), 'Slot order saved.');
  const autofillSlots = () => run(() => autofillPlannerSlots(token, panel.id), r => {
    const names = Object.fromEntries((data?.players || []).map(p => [p.id, p.name]));
    return r.unplaced?.length
      ? `Order reset. No room for: ${r.unplaced.map(id => names[id] || `#${id}`).join(', ')}.`
      : 'Order reset.';
  });
  const fillWeek = async body => {
    const ok = await run(() => fillPlannerWeek(token, isoYear, isoWeek, body), r => {
      const skipped = r.skipped_blocks?.length ? ` ${r.skipped_blocks.length} block(s) skipped: ${r.skipped_blocks.map(s => `${s.title} (${s.reason})`).join('; ')}.` : '';
      return `${r.blocks_created} block${r.blocks_created === 1 ? '' : 's'} added from the scenario template.${skipped}`;
    });
    if (ok) setFillOpen(false);
  };
  const saveTemplate = async body => {
    const ok = await run(() => savePlannerTemplate(token, isoYear, isoWeek, body),
      r => `Saved as Scenario ${r.scenario_code} · v${r.version}. Next time you fill from ${r.scenario_code}, these times are the default.`);
    if (ok) setSaveTplOpen(false);
  };

  const saveGroups = async body => {
    const ok = await run(() => setPlannerDayGroups(token, groupsDate, body), 'Groups saved.');
    if (ok) setGroupsDate(null);
  };

  const removeBlock = async () => {
    if (!window.confirm(`Delete "${panel.title}"?`)) return;
    const ok = await run(() => deletePlannerBlock(token, panel.id), 'Block deleted.');
    if (ok) setPanel(null);
  };
  const duplicate = async dates => {
    const ok = await run(() => duplicatePlannerBlock(token, panel.id, dates), r => {
      const skipped = r.skipped?.length ? ` Skipped: ${r.skipped.map(s => `${fmtDayShort(s.date)} (${s.reason})`).join('; ')}.` : '';
      return `Duplicated to ${r.created.length} day${r.created.length === 1 ? '' : 's'}.${skipped}`;
    });
    if (ok) setPanel(null);
  };
  const clearWeek = () => {
    const n = data.blocks.length;
    if (!window.confirm(`Remove all ${n} block${n === 1 ? '' : 's'} from week ${isoWeek}? Groups, day types and notes stay.${data.status === 'published'
      ? ' Players keep seeing the published schedule until you publish again.' : ''}`)) return;
    run(() => clearPlannerWeek(token, isoYear, isoWeek), r => `Week ${isoWeek} cleared: ${r.removed} block${r.removed === 1 ? '' : 's'} removed.`);
  };
  const clearDay = date => {
    const n = data.blocks.filter(b => b.date === date).length;
    if (!window.confirm(`Remove all ${n} block${n === 1 ? '' : 's'} from ${fmtDayShort(date)}? Groups, day type and note stay.${data.status === 'published'
      ? ' Players keep seeing the published schedule until you publish again.' : ''}`)) return;
    run(() => clearPlannerDay(token, date), r => `${fmtDayShort(date)} cleared: ${r.removed} block${r.removed === 1 ? '' : 's'} removed.`);
  };
  const publish = () => {
    const first = data.status !== 'published';
    if (!window.confirm(first
      ? `Publish week ${isoWeek}? Players will see their schedule for this week.`
      : `Publish the ${data.changes_since_publish} change${data.changes_since_publish === 1 ? '' : 's'} to week ${isoWeek}? Players will see the updated schedule, with moved blocks marked "moved, was …".`)) return;
    run(() => publishPlannerWeek(token, isoYear, isoWeek), `Week ${isoWeek} published.`);
  };

  // Blocks per day, with the drag preview applied.
  const blocksByDay = useMemo(() => {
    const out = {};
    (data?.days || []).forEach(d => { out[d.date] = { placed: [], unresolved: [] }; });
    (data?.blocks || []).forEach(b => {
      if (preview && preview.id === b.id) {
        out[preview.date]?.placed.push({ ...b, date: preview.date, _start: preview.start, _end: preview.end, lane: 0, lanes: 1, _dragging: true });
        return;
      }
      if (b.resolved_start == null) out[b.date]?.unresolved.push(b);
      else out[b.date]?.placed.push({ ...b, _start: toMin(b.resolved_start), _end: toMin(b.resolved_end) });
    });
    return out;
  }, [data, preview]);

  if (error === 'session') return <Message>Your session has expired. Sign out and sign in again to use the Planner.</Message>;
  if (error === 'access') return <Message>You don't have access to the Planner.</Message>;
  if (error && !data) return <Message>Could not load the week: {error}</Message>;
  if (!data) return <Message>Loading…</Message>;

  const thisWeek = isoWeekOf(localToday());
  const hours = [];
  for (let m = Math.ceil(GRID_START / 60) * 60; m <= GRID_END; m += 60) hours.push(m);
  const gridHeight = (GRID_END - GRID_START) * PPM;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <button className="pl-btn" aria-label="Previous week" onClick={() => { const w = shiftWeek(isoYear, isoWeek, -1); onChangeWeek(w.isoYear, w.isoWeek); }}><ChevronLeft size={16} /></button>
        <div style={{ minWidth: 210, textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Week {isoWeek}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtRange(data.monday, data.sunday)} {data.sunday.slice(0, 4)}</div>
        </div>
        <button className="pl-btn" aria-label="Next week" onClick={() => { const w = shiftWeek(isoYear, isoWeek, 1); onChangeWeek(w.isoYear, w.isoWeek); }}><ChevronRight size={16} /></button>
        <button className="pl-btn" onClick={() => onChangeWeek(thisWeek.isoYear, thisWeek.isoWeek)}>This week</button>
        <span style={{ fontSize: 13, color: data.status === 'published' ? 'var(--pl-ok)' : 'var(--text-secondary)', marginLeft: 8 }}>
          {STATUS_TEXT[data.status]}
          {data.status === 'published' && (data.changes_since_publish
            ? ` · ${data.changes_since_publish} change${data.changes_since_publish === 1 ? '' : 's'} not published yet (players still see the published version)`
            : ' · players see this schedule')}
        </span>
        <div style={{ flex: 1 }} />
        <button className="pl-btn" disabled={exporting || !data.blocks.length} onClick={() => setExporting(true)}
          title="The players' sheet: schedule, Group 1 / Group 2, video topics. Staff-only items are left out.">
          <FileDown size={14} /> {exporting ? 'Exporting…' : 'Export PDF'}
        </button>
        <button className="pl-btn" onClick={() => setFeedOpen(true)}><CalendarPlus size={14} /> Calendar</button>
        {canEdit && <button className="pl-btn" onClick={() => setFillOpen(true)}><LayoutTemplate size={14} /> Fill from scenario…</button>}
        {canEdit && <button className="pl-btn" onClick={() => setSaveTplOpen(true)} disabled={!data.blocks.length}><Save size={14} /> Save as template…</button>}
        {canEdit && <button className="pl-btn" onClick={clearWeek} disabled={busy || !data.blocks.length}><Eraser size={14} /> Clear week</button>}
        {canEdit && <button className="pl-btn" onClick={() => setCategoriesOpen(true)}><Tags size={14} /> Categories</button>}
        {canEdit && (
          <button className="pl-btn pl-btn--primary" disabled={busy || (data.status === 'published' && !data.changes_since_publish)} onClick={publish}>
            <Send size={14} /> {data.status === 'published' ? 'Publish changes' : 'Publish'}
          </button>
        )}
      </div>

      {notice && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 10, borderRadius: 8, fontSize: 13, border: `1px solid ${notice.kind === 'err' ? 'var(--pl-err)' : 'var(--pl-ok)'}` }}>
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button className="pl-btn" aria-label="Dismiss" onClick={() => setNotice(null)} style={{ minHeight: 26, padding: '2px 6px' }}><X size={13} /></button>
        </div>
      )}

      {data.removed_since_publish.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
          Removed, but players still see these until you publish: {data.removed_since_publish.map(r => `${r.title} (${fmtDayShort(r.date)}${r.start ? ` ${r.start}` : ''})`).join(', ')}
        </div>
      )}

      {canEdit && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
          Click an empty spot to add a block · click a block to edit · drag to move · drag the bottom edge to change the end time.
          {data.status === 'published' && " Tagged blocks (NEW / MOVED / EDITED) are changes players don't see until you publish them."}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '52px repeat(7, minmax(128px, 1fr))', columnGap: 6, minWidth: 1000 }}>
          {/* Day headers */}
          <div />
          {data.days.map(d => (
            <DayHeader key={d.date} day={d} unresolved={blocksByDay[d.date].unresolved} canEdit={canEdit}
              onAdd={() => setPanel({ date: d.date, start_time: '10:00', end_time: '11:00' })}
              onCopy={() => setCopyDay(d.date)}
              onClear={busy || !data.blocks.some(b => b.date === d.date) ? null : () => clearDay(d.date)}
              onGroups={() => setGroupsDate(d.date)}
              onOpenBlock={setPanel} isToday={d.date === data.today} />
          ))}

          {/* Time axis */}
          <div style={{ position: 'relative', height: gridHeight }}>
            {hours.map(m => (
              <span key={m} style={{ position: 'absolute', top: (m - GRID_START) * PPM - 7, right: 6, fontSize: 11, color: 'var(--text-muted)', fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>{fromMin(m)}</span>
            ))}
          </div>

          {/* Day columns */}
          {data.days.map(d => (
            <div key={d.date}
              ref={el => { columnRefs.current[d.date] = el; }}
              onClick={e => onColumnClick(e, d.date)}
              style={{
                position: 'relative', height: gridHeight, borderRadius: 8, border: '1px solid var(--border)',
                background: d.type === 'off' ? 'var(--pl-off-bg)' : 'var(--bg-card)', cursor: canEdit ? 'copy' : 'default',
                backgroundImage: `repeating-linear-gradient(to bottom, transparent 0, transparent ${60 * PPM - 1}px, var(--border) ${60 * PPM - 1}px, var(--border) ${60 * PPM}px)`,
                backgroundPosition: `0 ${(Math.ceil(GRID_START / 60) * 60 - GRID_START) * PPM}px`,
              }}>
              {blocksByDay[d.date].placed.map(b => (
                <BlockBox key={b.id} block={b} cats={cats} canEdit={canEdit}
                  onPointerDown={(e, mode) => onBlockPointerDown(e, b, mode)}
                  onPointerMove={onBlockPointerMove}
                  onPointerUp={onBlockPointerUp}
                  onClick={() => onBlockClick(b)} />
              ))}
            </div>
          ))}
        </div>
      </div>

      <Legend categories={data.categories} />

      {panel && (
        <BlockPanel key={panel.id || `${panel.date}-${panel.start_time}`}
          block={panel.id ? (data.blocks.find(b => b.id === panel.id) || panel) : panel} week={data} canEdit={canEdit} busy={busy}
          onClose={() => setPanel(null)} onSave={saveBlock} onDelete={removeBlock} onDuplicate={duplicate}
          onSaveSlots={saveSlots} onAutofillSlots={autofillSlots} />
      )}

      {groupsDate && (
        <GroupsPanel key={groupsDate} token={token} day={data.days.find(d => d.date === groupsDate)} players={data.players}
          canEdit={canEdit} busy={busy} onClose={() => setGroupsDate(null)} onSave={saveGroups} />
      )}

      {exporting && (
        <PlannerExport week={data} onDone={err => {
          setExporting(false);
          setNotice(err ? { kind: 'err', text: `PDF export failed: ${err}` } : { kind: 'ok', text: `Week ${isoWeek} exported as PDF.` });
        }} />
      )}
      {feedOpen && (
        <Overlay title="Club schedule in your calendar" onClose={() => setFeedOpen(false)}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Subscribe your phone or laptop calendar to every published week of the club schedule (2 weeks back, 6 weeks ahead),
            staff-only items included. Changes appear by themselves once they are published.
          </div>
          <CalendarFeedLink token={token} audienceText="published weeks, staff view" />
        </Overlay>
      )}

      {fillOpen && (
        <FillFromScenarioDialog token={token} isoYear={isoYear} isoWeek={isoWeek} busy={busy} onClose={() => setFillOpen(false)} onFill={fillWeek} />
      )}
      {saveTplOpen && (
        <SaveAsTemplateDialog token={token} week={data} busy={busy} onClose={() => setSaveTplOpen(false)} onSave={saveTemplate} />
      )}

      {categoriesOpen && (
        <CategoriesDialog token={token} onClose={() => setCategoriesOpen(false)} onChanged={load} />
      )}

      {copyDay && (
        <CopyDayDialog target={copyDay} week={data} busy={busy} onClose={() => setCopyDay(null)}
          onCopy={async (sourceDate, replace) => {
            const ok = await run(() => copyPlannerDay(token, copyDay, { sourceDate, replace }), r => {
              const skipped = r.skipped?.length ? ` Skipped: ${r.skipped.map(s => `${s.title} (${s.reason})`).join('; ')}.` : '';
              return `Copied ${r.created} block${r.created === 1 ? '' : 's'} from ${fmtDayShort(sourceDate)}.${skipped}`;
            });
            if (ok) setCopyDay(null);
          }} />
      )}
    </div>
  );
}

function Message({ children }) {
  return <div style={{ padding: '48px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>{children}</div>;
}

function DayHeader({ day, unresolved, canEdit, onAdd, onCopy, onClear, onGroups, onOpenBlock, isToday }) {
  const groupCount = day.groups[1].length + day.groups[2].length;
  const game = day.type === 'game';
  return (
    <div style={{
      borderRadius: 8, padding: '7px 8px', marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12,
      background: game ? 'var(--pl-game-bg)' : day.type === 'off' ? 'var(--pl-off-bg)' : 'var(--bg-card)',
      color: game ? 'var(--pl-game-fg)' : 'var(--text-primary)', border: '1px solid var(--border)',
      boxShadow: isToday ? '0 0 0 2px var(--pl-today)' : 'none',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>{fmtDayShort(day.date)}</span>
        {day.label.text && <span className="pl-pill" style={game ? { background: 'rgba(127,127,127,0.25)', color: 'inherit' } : undefined}>{day.label.text}</span>}
      </div>
      {day.fixtures.map(f => (
        <span key={f.id} style={{ fontWeight: 600 }}>{fixtureLine(f)} · {f.kickoff_time || 'KO TBC'}</span>
      ))}
      {day.type === 'off' && <span className="pl-tag pl-tag--off" style={{ marginTop: 0 }}>OFF</span>}
      {(day.scenario || day.source) && (
        <span style={{ fontSize: 11, opacity: 0.8 }}>
          {day.scenario ? `Scenario ${day.scenario.code} · ${day.scenario.label}` : ''}
          {day.source ? `${day.scenario ? ' · ' : ''}from ${day.source.scenario_code} v${day.source.version}` : ''}
        </span>
      )}
      {day.note && <span style={{ opacity: 0.85 }}>{day.note}</span>}
      {unresolved.map(b => (
        <button key={b.id} type="button" onClick={() => onOpenBlock(b)}
          style={{ textAlign: 'left', font: 'inherit', fontSize: 11, color: 'inherit', background: 'var(--pl-warn-bg)', border: '1px dashed var(--pl-warn-border)', borderRadius: 6, padding: '3px 6px', cursor: 'pointer' }}>
          {b.title}: {UNRESOLVED_TEXT(b.unresolved)}
        </button>
      ))}
      {day.has_rotation && day.not_in_group.length > 0 && (
        <button type="button" onClick={onGroups}
          style={{ textAlign: 'left', font: 'inherit', fontSize: 11, color: 'inherit', background: 'var(--pl-warn-bg)', border: '1px dashed var(--pl-warn-border)', borderRadius: 6, padding: '3px 6px', cursor: 'pointer' }}>
          Rotation: {day.not_in_group.length} player{day.not_in_group.length === 1 ? '' : 's'} not in a group
        </button>
      )}
      {day.groups_changed && <span style={{ fontSize: 11, color: 'var(--pl-warn-border)' }}>Groups changed · not published</span>}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(canEdit || groupCount > 0) && (
          <button className="pl-btn" style={{ minHeight: 24, padding: '2px 7px', fontSize: 11 }} onClick={onGroups}>
            <Users size={12} /> {groupCount ? `G1 ${day.groups[1].length} · G2 ${day.groups[2].length}` : 'Groups'}
          </button>
        )}
        {canEdit && <button className="pl-btn" style={{ minHeight: 24, padding: '2px 7px', fontSize: 11 }} onClick={onAdd}><Plus size={12} /> Block</button>}
        {canEdit && <button className="pl-btn" style={{ minHeight: 24, padding: '2px 7px', fontSize: 11 }} onClick={onCopy}><Copy size={12} /> Copy from…</button>}
        {canEdit && onClear && <button className="pl-btn" style={{ minHeight: 24, padding: '2px 7px', fontSize: 11 }} onClick={onClear}><Eraser size={12} /> Clear</button>}
      </div>
    </div>
  );
}

function changeTag(change) {
  if (!change) return null;
  if (change.kind === 'new') return 'NEW';
  if (change.kind === 'edited') return 'EDITED';
  const was = change.was;
  return `MOVED · was ${was.start || '?'}`;
}

function BlockBox({ block, cats, canEdit, onPointerDown, onPointerMove, onPointerUp, onClick }) {
  const cat = cats[block.category] || FALLBACK_CATEGORY;
  const top = (block._start - GRID_START) * PPM;
  const height = Math.max(14, (block._end - block._start) * PPM - 2);
  const staffOnly = block.audience.includes('staff');
  const slotInfo = block.slot_minutes
    ? `${block.slots.filter(x => x.fits).length} slots · ${block.slot_minutes} min${block.slot_unplaced.length ? ` · ${block.slot_unplaced.length} don't fit` : ''}`
    : '';
  const sub = block.category === 'routine' ? routineSummary(block.routine) : [block.location, slotInfo].filter(Boolean).join(' · ');
  const isRotation = !!block.rotation && block.rotation_segments.length > 0;
  const hint = audienceHint(block.audience);
  const treatment = block.category === 'arrival' && block.arrival_purpose === 'treatment';
  const tag = changeTag(block.change);
  const time = `${fromMin(block._start)}–${fromMin(block._end)}`;
  const tall = height > 34 && !isRotation;
  return (
    <button type="button"
      onPointerDown={e => onPointerDown(e, 'move')}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={onClick}
      aria-label={`${block.title}, ${time}${treatment ? ', optional medical treatment' : ''}${staffOnly ? ', staff only' : ''}`}
      style={{
        position: 'absolute', top, height, left: `calc(${(block.lane / block.lanes) * 100}% + 2px)`, width: `calc(${100 / block.lanes}% - 4px)`,
        background: cat.bg, color: cat.fg, border: 'none', borderRadius: 6, padding: '2px 5px', boxSizing: 'border-box',
        overflow: 'hidden', textAlign: 'left', font: 'inherit', fontSize: 11, lineHeight: 1.25, cursor: canEdit ? 'grab' : 'pointer',
        display: 'flex', flexDirection: 'column', gap: 1, touchAction: canEdit ? 'none' : 'auto',
        boxShadow: block._dragging ? '0 4px 14px rgba(0,0,0,0.35)' : 'inset 0 0 0 1px rgba(0,0,0,0.08)',
        opacity: block._dragging ? 0.9 : 1, zIndex: block._dragging ? 5 : 1,
        backgroundImage: staffOnly ? 'repeating-linear-gradient(135deg, transparent 0 6px, rgba(0,0,0,0.16) 6px 9px)' : 'none',
      }}>
      {isRotation && <RotationLanes block={block} cats={cats} />}
      <span style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', position: 'relative' }}>{block.title}</span>
      {tall && <span style={{ opacity: 0.9, fontFamily: "'IBM Plex Mono', ui-monospace, monospace", fontSize: 10 }}>{time}{block.timing === 'kickoff' ? ' · KO-anchored' : ''}</span>}
      {tall && sub && <span style={{ opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{sub}</span>}
      {block.category === 'gym' && block.gym[0] && height > 22 && !isRotation && (
        <span title={gymCountsText(block.gym[0])} style={{ fontWeight: 600, fontSize: 10, lineHeight: 1.3, whiteSpace: 'normal' }}>{gymCountsText(block.gym[0])}</span>
      )}
      {treatment && height > 30 && (
        <span style={{ alignSelf: 'flex-start', fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', background: '#a33a4f', color: '#fff', padding: '1px 4px', borderRadius: 3 }}>MEDICAL TREATMENT</span>
      )}
      {(staffOnly || hint || tag) && height > 48 && (
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 'auto' }}>
          {staffOnly && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '1px 4px', borderRadius: 3 }}>STAFF ONLY</span>}
          {!staffOnly && hint && <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(255,255,255,0.7)', color: '#15171a', padding: '1px 4px', borderRadius: 3 }}>{hint}</span>}
          {tag && <span style={{ fontSize: 9, fontWeight: 800, background: '#f59e0b', color: '#15171a', padding: '1px 4px', borderRadius: 3 }}>{tag}</span>}
        </span>
      )}
      {(staffOnly || tag) && height <= 48 && (
        <span aria-hidden="true" style={{ position: 'absolute', top: 3, right: 3, width: 7, height: 7, borderRadius: '50%', background: tag ? '#f59e0b' : '#15171a' }} />
      )}
      {canEdit && (
        <span aria-hidden="true" onPointerDown={e => onPointerDown(e, 'resize')}
          style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 7, cursor: 'ns-resize' }} />
      )}
    </button>
  );
}

// A rotation block drawn as two lanes (G1 | G2), each split at the swap time into its two stations.
function RotationLanes({ block, cats }) {
  const total = toMin(block.resolved_end) - toMin(block.resolved_start);
  return (
    <span aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', gap: 2, padding: 2, boxSizing: 'border-box' }}>
      {[1, 2].map(g => (
        <span key={g} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          {block.rotation_segments.filter(sg => sg.group === g).map(sg => {
            const c = cats[sg.category] || FALLBACK_CATEGORY;
            const share = (toMin(sg.end) - toMin(sg.start)) / total;
            return (
              <span key={sg.station} style={{ flex: share, background: c.bg, color: c.fg, borderRadius: 4, padding: '14px 4px 2px', overflow: 'hidden', fontSize: 10, lineHeight: 1.2, minHeight: 0 }}>
                <strong>G{g}</strong> {sg.title}
                {sg.category === 'gym' && block.gym.find(x => x.group === g) && (
                  <span style={{ display: 'block', fontWeight: 600 }}>{gymCountsText(block.gym.find(x => x.group === g))}</span>
                )}
              </span>
            );
          })}
        </span>
      ))}
    </span>
  );
}

function Legend({ categories }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12, fontSize: 11, color: 'var(--text-secondary)' }}>
      {categories.filter(c => !c.archived_at).map(c => (
        <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: c.bg, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)' }} />{c.label}
        </span>
      ))}
    </div>
  );
}

function CopyDayDialog({ target, week, busy, onClose, onCopy }) {
  const [source, setSource] = useState(addDays(target, -1));
  const targetHasBlocks = week.blocks.some(b => b.date === target);
  const [replace, setReplace] = useState(false);
  return (
    <Overlay title={`Copy a day onto ${fmtDayShort(target)}`} onClose={onClose}>
      <label className="pl-label">Copy all blocks from
        <input className="pl-input" type="date" value={source} onChange={e => setSource(e.target.value)} />
      </label>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Any day works, also from an earlier week. Kickoff-anchored blocks only copy onto a game day.</div>
      {targetHasBlocks && (
        <div role="radiogroup" aria-label="Existing blocks" style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          <label style={{ display: 'flex', gap: 8 }}><input type="radio" checked={!replace} onChange={() => setReplace(false)} /> Keep this day's blocks and add the copied ones</label>
          <label style={{ display: 'flex', gap: 8 }}><input type="radio" checked={replace} onChange={() => setReplace(true)} /> Replace this day's blocks</label>
        </div>
      )}
      <button className="pl-btn pl-btn--primary" style={{ alignSelf: 'flex-start' }} disabled={busy || !source || source === target} onClick={() => onCopy(source, replace)}>Copy day</button>
    </Overlay>
  );
}
